<template>
  <v-fade-transition leave-absolute>
    <!-- Pending -->
    <div v-if="activityFetch.status.value === 'pending'" class="d-flex flex-column" style="height: 100vh">
      <v-progress-linear indeterminate color="primary" />
      <div class="flex-grow-1 d-flex justify-center align-center">
        <span class="text-h4 text-medium-emphasis">
          {{ $t('Loading') }}
        </span>
      </div>
    </div>

    <!-- Route -->
    <ActivitySetup v-else-if="activity" :activity="activity">
      <NuxtPage :activity="activity" />
    </ActivitySetup>

    <!-- 404 -->
    <v-container v-else>
      <v-empty-state
        :headline="$t('Error404')"
        icon="mdi-transmission-tower-off"
        :text="`${$t('Error')}: ${activityFetch.error.value ? activityFetch.error.value.message : 'unknown'}`"
      >
        <template #actions>
          <v-btn :to="{ name: Routes.index }" color="primary" elevation="0" prepend-icon="mdi-arrow-left">
            {{ $t('ButtonBack') }}
          </v-btn>
        </template>
      </v-empty-state>
    </v-container>
  </v-fade-transition>
</template>

<script lang="ts">
import type { ActivityRead } from '@gridcon-energy/schaltungstool-backend'
import { defineComponent, toValue } from 'vue'
import { definePageMeta } from '#imports'
import { Routes } from '~/_routes'
import { injectBackendClient } from '~/api/backendClients'
import ActivitySetup from '~/components/Activity/ActivitySetup.vue'
import { useRoute } from '#vue-router'
import { useRouteParams } from '@vueuse/router'
import { useAsyncData } from '#app'

export default defineComponent({
  name: 'ActivityPages',
  components: {
    ActivitySetup,
  },
  setup() {
    const backend = injectBackendClient()
    const route = useRoute()

    definePageMeta({
      layout: 'application',
      // middleware: 'sidebase-auth',
      validate(route) {
        const activity = parseInt(route.params.activity as string)
        return !isNaN(activity) && activity > 0 // param is a positive integer
      },
    })

    const activityId = useRouteParams('activity', String(route.query.activity || 0), { transform: parseInt })
    const id = toValue(activityId)

    /**
     * Load Activity
     */
    const activityFetch = useAsyncData<ActivityRead>(
      async () => {
        const response = await backend.activity.getActivityById(id)
        return response.data
      },
      { default: () => null },
    )

    return {
      backend,
      Routes,
      activityFetch,
    }
  },
  computed: {
    activity() {
      return this.activityFetch.data.value
    },
  },
})
</script>

<style lang="scss" scoped>
:deep(.v-badge__badge) {
  cursor: pointer;
  display: flex;
}
</style>
